import { Circle, MapContainer, Pane, TileLayer, ZoomControl, useMapEvents } from 'react-leaflet';

import 'leaflet/dist/leaflet.js';
// import { FullscreenControl } from 'react-leaflet-fullscreen';
import 'react-leaflet-fullscreen/styles.css';
import { Loading } from 'components/loading';
import { Box } from '@mui/material';
import { Styled } from './index.styles';
import { useEffect, useState } from 'react';
// import { mediaBreakPoints, useMediaScreen } from 'common/theme/media';
import HotelGroupPin from '../hotel-group-pin';
import MapTicketCard from '../map-ticket-card';
import useHotelService from 'services/hotel/index.api';
import moment from 'moment';
import i18n from 'assets/i18n';
import { Info } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import { t } from 'i18next';

interface HotelListMapProps {
    data: any[];
    fullscreenCallback?: (data: boolean) => void;
    isMobile?: boolean;
    params?: any;
    mapFullScreen?: boolean;
}

const HotelListMap = (props: HotelListMapProps) => {
    const { searchLocation } = useHotelService();

    const { currentCurrency } = useSelector((state: any) => state.currency);

    let position = [0, 0];
    const positions = props.data.map((item) => [item.latitude, item.longitude]);

    if (positions.length > 0) {
        const totalLat = positions.reduce((sum, position) => sum + position[0], 0);
        const totalLng = positions.reduce((sum, position) => sum + position[1], 0);

        const centerLat = totalLat / positions.length;
        const centerLng = totalLng / positions.length;

        position = [centerLat, centerLng];
    }

    const [map, setMap] = useState<any>();
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<any>(props.data);

    // const media = useMediaScreen();

    const [mapFullScreen, setMapFullScreen] = useState<boolean>(props.mapFullScreen || false);
    const [hotelMarker, setHotelMarker] = useState<any>();

    useEffect(() => {
        const handleFullscreenChange = () => {
            setMapFullScreen(!mapFullScreen);
            setTimeout(() => {
                if (!mapFullScreen) {
                    props.fullscreenCallback && props.fullscreenCallback(true);
                } else {
                    props.fullscreenCallback && props.fullscreenCallback(false);
                }
            });
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, [mapFullScreen, props.fullscreenCallback]);

    const mapZoomConvertToDistance = (zoom: number): number => {
        let result;

        switch (zoom) {
            case 18:
                result = 0.1;
                break;
            case 17:
                result = 0.3;
                break;
            case 16:
                result = 0.6;
                break;
            case 15:
                result = 1.2;
                break;
            case 14:
                result = 2.4;
                break;
            case 13:
                result = 4.8;
                break;
            case 12:
                result = 9.6;
                break;
            case 11:
                result = 19.2;
                break;
            case 10:
                result = 38.4;
                break;
            case 9:
                result = 76.8;
                break;
            case 8:
                result = 153.6;
                break;
            case 7:
                result = 307.2;
                break;
            case 6:
                result = 615.4;
                break;
            case 5:
                result = 1230.8;
                break;
            case 4:
                result = 2461.6;
                break;
            default:
                result = 4923.2;
                break;
        }

        return result;
    };

    const MapEvents = () => {
        useMapEvents({
            click: () => {
                setHotelMarker(null);
            },
        });
        return null;
    };

    const NoResultBox = () => {
        return (
            <div className="info-alert">
                <div className="icon">
                    <Info />
                </div>
                <div className="text">
                    <b>{t('hotel.widgets.map.noResultText')}</b>
                    <br></br>
                </div>
            </div>
        );
    };

    const searchLocationData = (mapF: any) => {
        setLoading(true);

        let body = {
            latitude: mapF.getCenter().lat || position[0],
            longitude: mapF.getCenter().lng || position[1],
            distance: mapZoomConvertToDistance(mapF._zoom),
            start_date: props.params.start_date ? moment(props.params.start_date).format('YYYY-MM-DD') : '2024-04-10',
            end_date: props.params.end_date ? moment(props.params.end_date).format('YYYY-MM-DD') : '2024-04-15',
            party: [
                {
                    adults: props.params.adults && props.params.adults,
                    children: props.params.children && props.params.children,
                },
            ],
            lang: i18n.language,
            currency: currentCurrency,
            user_country: 'TR',
            device_type: 'Desktop',
            is_filter: true,
        };
        searchLocation(body)
            .then((data: any) => {
                if (data.success) {
                    const res = data.data;

                    setData(res);
                }

                setTimeout(() => {
                    setLoading(false);
                }, 700);
            })
            .catch((err: any) => {
                console.log('err', err);
                setTimeout(() => {
                    setLoading(false);
                }, 700);
            });
    };

    return (
        <Styled degre={0}>
            {props ? (
                <Box display="flex" flexDirection="row">
                    {!props.isMobile && (
                        <Box className="list-box">
                            {data?.length > 0 ? (
                                <>
                                    <div className="list-title">
                                        {data[0]?.location.split('/')[1]} {t('hotel.widgets.map.title')}
                                    </div>
                                    {data?.map((item: any) => (
                                        <MapTicketCard
                                            data={item}
                                            params={props.params}
                                            hotelDetailCallBack={(item: any) => {
                                                setHotelMarker(item);
                                                map.flyTo([item?.latitude, item?.longitude], 18);
                                            }}
                                            isActiveId={hotelMarker?.id}
                                        />
                                    ))}
                                </>
                            ) : (
                                <NoResultBox />
                            )}
                        </Box>
                    )}
                    <Box display="flex" flexDirection="column" width={'auto'} flex={1} position={'relative'}>
                        <MapContainer
                            ref={setMap}
                            id="map"
                            center={position as [number, number]}
                            zoom={12}
                            minZoom={5}
                            zoomControl={false}
                            style={{ height: mapFullScreen ? '100%' : '70vh', width: '100%' }}
                        >
                            <MapEvents />
                            <TileLayer
                                attribution={`Google Maps`}
                                url="https://www.google.com/maps/vt?lyrs=m@189&hl=tr&x={x}&y={y}&z={z}"
                            />
                            <ZoomControl position="bottomright" />
                            {loading && map && (
                                <Pane
                                    name="custom-circle"
                                    className="custom-circle"
                                    key={`${map.getCenter().lat}-${map.getCenter().lng}-${map._zoom}`}
                                >
                                    <Circle
                                        stroke={false}
                                        center={[map.getCenter().lat, map.getCenter().lng]}
                                        radius={mapZoomConvertToDistance(Number(map._zoom)) * 1000}
                                    />
                                    <Circle
                                        stroke={false}
                                        center={[map.getCenter().lat, map.getCenter().lng]}
                                        radius={mapZoomConvertToDistance(Number(map._zoom)) * 850}
                                    />
                                </Pane>
                            )}
                            <HotelGroupPin
                                data={data}
                                mapRef={map}
                                isMobile={props.isMobile}
                                params={props.params}
                                marker={hotelMarker}
                            />
                            {/* {media.width > mediaBreakPoints.xltablet && (
                                <FullscreenControl
                                    forceSeparateButton={true}
                                    forcePseudoFullscreen={false}
                                    position="topright"
                                />
                            )} */}
                        </MapContainer>
                        <div className="search-button">
                            <LoadingButton
                                variant="contained"
                                fullWidth
                                color={'inherit'}
                                onClick={() => searchLocationData(map)}
                                loading={loading}
                                disabled={loading}
                            >
                                {loading ? <span>&nbsp;</span> : t('hotel.widgets.map.searchAreaButton')}
                            </LoadingButton>
                        </div>
                    </Box>
                </Box>
            ) : (
                <Box display="flex" flexDirection="row" style={{ height: '70vh', width: '100%' }}>
                    <Loading />
                </Box>
            )}
        </Styled>
    );
};

export default HotelListMap;
